import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useCompanyName } from "@/lib/company";
import { useFlags } from "@/lib/flags";
import { useGitHub } from "@/lib/github";
import { APP_NAME } from "@/config";
import { Page, PageHeader, Card, StatusBadge, PrivacyNote, Loading, usd, int, plural, ago } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FolderGit2, Plus, GitBranch, KeyRound, Copy, ExternalLink, Check, X, RefreshCw, Github, TriangleAlert } from "lucide-react";

// design_handoff_crewbench/screens/Projects.dc.html: each repository, what's been done on it this month, and whether
// sessions can read, push and open pull requests there.

// a GitHub project's state: ready, needs attention, no deploy key yet, or not checked
function accessState(p) {
  if (p.remote_kind !== "github") return "local";
  if (p.auth === "deploy_key" && !p.deploy_key) return "no-key";
  const a = p.access;
  if (!a) return "unchecked";
  return a.read && a.write && a.base_branch_found && a.pull_requests === "ok" ? "ready" : "attention";
}
const BADGE = {
  ready: ["Ready", "info"], attention: ["Needs attention", "warning"], "no-key": ["No deploy key", "warning"],
  unchecked: ["Not checked", "muted"], local: ["Demo repository", "muted"],
};

function Check1({ ok, yes, no }) {
  return (
    <span className={`flex items-center gap-1.5 ${ok ? "text-gr-success" : "text-gr-warning"}`}>
      {ok ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}{ok ? yes : no}
    </span>
  );
}

// a project's numbers (GET /projects: `stats`, the founder's): open objectives, this month's sessions and spend, and
// its shared memory with the proposals waiting
const month = () => new Date().toLocaleDateString("en-GB", { month: "long" });

function Stats({ p }) {
  const st = p.stats;
  if (!st) return null;
  const cell = (label, value, extra) => (
    <div className="flex-[1_1_150px] bg-card px-3.5 py-2.5 flex flex-col gap-0.5">
      <span className="text-xs uppercase tracking-[0.09em] text-muted-foreground">{label}</span>
      <span className="text-[15px] leading-[22px]">{value}</span>{extra}
    </div>
  );
  return (
    <div className="flex flex-wrap gap-px bg-gr-hairline border rounded-lg overflow-hidden tabular-nums" data-testid={`project-stats-${p.name}`}>
      {cell("Objectives", st.objectives_open ? `${int(st.objectives_open)} open` : "None yet")}
      {cell("Sessions", st.sessions_month ? `${int(st.sessions_month)} this month` : "None yet")}
      {cell(`Spent in ${month()}`, usd(st.spent_month_usd))}
      {cell("Shared memory", st.memory_entries ? plural(st.memory_entries, "entry", "entries") : "Empty",
        st.proposals_waiting > 0 && (
          <Link to={`/founder/memory?project=${p.id}`} className="text-sm text-gr-warning underline underline-offset-2 hover:opacity-50">
            {plural(st.proposals_waiting, "proposal")} waiting
          </Link>
        ))}
    </div>
  );
}

function GitHubAccess({ p, state }) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["projects"] });
  const check = useMutation({
    mutationFn: async () => (await api.post(`/projects/${p.id}/check-access`)).data,
    onSuccess: refresh,
    onError: (e) => toast.error(apiErr(e)),
  });
  const makeKey = useMutation({
    mutationFn: async (rotate) => (await api.post(`/projects/${p.id}/deploy-key`, { rotate })).data,
    onSuccess: (_d, rotate) => {
      refresh();
      toast.success(rotate ? "New deploy key made. Add it on GitHub and remove the old one." : "Deploy key ready. Add it on GitHub.");
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const key = p.deploy_key;
  const copy = async () => {
    try { await navigator.clipboard.writeText(key.public_key); toast.success("Public key copied."); }
    catch { toast.error("Copy failed. Select the key and copy it."); }
  };
  const a = p.access;
  const problems = a ? [a.problem, a.pull_requests !== "ok" && a.pull_requests].filter(Boolean) : [];
  const viaKey = p.auth === "deploy_key";
  return (
    <div className="flex flex-col gap-2.5 pt-4 border-t" data-testid={viaKey ? `deploy-key-${p.name}` : `app-remote-${p.name}`}>
      <div className="flex flex-wrap justify-between items-baseline gap-3">
        <span className="font-semibold">GitHub access</span>
        <span className="text-sm text-muted-foreground">
          {state === "no-key" ? "Needs a deploy key first" : a?.checked_at ? `Checked ${ago(a.checked_at)}` : "Not checked yet"}
        </span>
      </div>
      {a && state !== "no-key" && (
        <div className="flex flex-wrap gap-x-[18px] gap-y-1.5 text-sm" data-testid={`access-${p.name}`}>
          <Check1 ok={a.read} yes="Can read" no="Can't read" />
          <Check1 ok={a.write} yes="Can push" no="Can't push" />
          <Check1 ok={a.base_branch_found} yes={`Branch ${p.base_branch} found`} no={`No branch ${p.base_branch}`} />
          <Check1 ok={a.pull_requests === "ok"} yes="Can open pull requests" no="Can't open pull requests" />
        </div>
      )}
      {state !== "no-key" && problems.map((t) => (
        <div key={t} className="flex items-start gap-1.5 text-sm text-gr-warning"><TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span className="[text-wrap:pretty]">{t}</span></div>
      ))}
      {!viaKey && <span className="text-sm text-muted-foreground">Connected through the {APP_NAME} GitHub App. No deploy key needed.</span>}
      {viaKey && key && (
        <div className="flex flex-col gap-2">
          {/* the key and its fingerprint keep the monospace: they're pasted into GitHub and checked against it */}
          <span className="flex items-center gap-1.5 text-sm text-muted-foreground"><KeyRound className="w-3.5 h-3.5 shrink-0" /><span className="font-mono [overflow-wrap:anywhere]">{key.fingerprint}</span></span>
          <textarea readOnly rows={2} value={key.public_key} aria-label="Public deploy key" data-testid={`deploy-key-public-${p.name}`}
            className="w-full px-2.5 py-2 rounded-lg border bg-background font-mono text-xs leading-4 resize-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring" />
          <span className="text-sm text-muted-foreground [text-wrap:pretty]">
            Made on your workspace server; the private half never leaves it. On GitHub, paste this key and tick "Allow write access" so sessions can push their branches.
          </span>
        </div>
      )}
      {state === "no-key" && (
        <span className="text-sm text-muted-foreground [text-wrap:pretty]">Your workspace server makes an SSH deploy key for this repository. The private half never leaves it.</span>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {state !== "no-key" && (
          <Button variant="outline" onClick={() => check.mutate()} disabled={check.isPending} data-testid={`check-access-${p.name}`}>
            <RefreshCw className={check.isPending ? "animate-spin" : ""} />Check access
          </Button>
        )}
        {viaKey && key && <>
          <Button variant="outline" onClick={copy}><Copy />Copy key</Button>
          <Button variant="outline" asChild><a href={p.deploy_key_settings_url} target="_blank" rel="noreferrer"><ExternalLink />Add on GitHub</a></Button>
          <button type="button" onClick={() => makeKey.mutate(true)} disabled={makeKey.isPending}
            className="ml-1 text-sm text-muted-foreground underline underline-offset-2 hover:opacity-50">Replace the key</button>
        </>}
        {state === "no-key" && (
          <Button variant="outline" onClick={() => makeKey.mutate(false)} disabled={makeKey.isPending} data-testid={`deploy-key-create-${p.name}`}>
            <KeyRound />Set up deploy key
          </Button>
        )}
      </div>
    </div>
  );
}

// next features #10: the founder limits an independent professional to some folders of the project, for sensitive
// work such as a security fix (backend/session_folders.py)
function FolderLimits({ p, coders }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);  // {coder_id, text, isNew}
  const shown = useRef(null);
  if (editing) shown.current = editing;
  const save = useMutation({
    mutationFn: async ({ coder_id, folders }) => (await api.put(`/projects/${p.id}/folders`, { coder_id, folders })).data,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      setEditing(null);
      toast.success(r.folders ? `Limited to ${plural(r.folders.length, "folder")}. Sessions they opened with other folders can't be unlocked again.`
        : "They see the whole repository again in their new sessions.");
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const limits = p.folders_by_coder || {};
  const limited = coders.filter((c) => limits[c.id]);
  const free = coders.filter((c) => !limits[c.id]);
  const nameOf = (c) => c.name || c.email;
  const e = shown.current;
  const folders = (e?.text || "").split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
  return (
    <div className="pt-4 border-t flex flex-col gap-2 text-sm" data-testid={`project-folders-${p.name}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-semibold">Folders</span>
        {free.length > 0 && <Button size="sm" variant="ghost" onClick={() => setEditing({ coder_id: free[0].id, text: "", isNew: true })}
          data-testid={`project-folders-add-${p.name}`}>Limit someone</Button>}
      </div>
      {limited.length === 0 ? (
        <span className="text-muted-foreground [text-wrap:pretty]">Everyone sees the whole repository. For sensitive work, such as a security fix, limit
          someone to some folders: only those reach the workspace server in their sessions.</span>
      ) : limited.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`project-folders-row-${c.email}`}>
          <span>{nameOf(c)}</span>
          <span className="font-mono text-[12px] text-muted-foreground min-w-0 [overflow-wrap:anywhere]">{limits[c.id].join(", ")}</span>
          <Button size="sm" variant="ghost" onClick={() => setEditing({ coder_id: c.id, text: limits[c.id].join("\n") })}>Edit</Button>
        </div>
      ))}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="gap-[18px]">
          <div className="flex flex-col gap-1.5">
            <DialogTitle>Folders {e?.isNew ? "for someone" : `for ${nameOf(coders.find((c) => c.id === e?.coder_id) || {})}`} in {p.name}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground [text-wrap:pretty]">
              Their new sessions on this project get only these folders' files; the rest of the repository never reaches the workspace server
              (only file names and the commit history do, as git needs them). Changes outside them are put back. Sessions they opened before
              with other folders can't be unlocked again.
            </DialogDescription>
          </div>
          <form className="flex flex-col gap-3.5" onSubmit={(ev) => { ev.preventDefault(); if (folders.length) save.mutate({ coder_id: e.coder_id, folders }); }}>
            {e?.isNew && (
              <div className="flex flex-col gap-1.5">
                <Label>Person</Label>
                <Select value={e.coder_id} onValueChange={(v) => setEditing({ ...e, coder_id: v })}>
                  <SelectTrigger data-testid="folders-coder"><SelectValue /></SelectTrigger>
                  <SelectContent>{free.map((c) => <SelectItem key={c.id} value={c.id}>{nameOf(c)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`folders-${p.id}`}>Folders, one per line</Label>
              <Textarea id={`folders-${p.id}`} rows={4} className="font-mono text-[12px]" placeholder={"services/auth\ndocs/security"}
                value={e?.text || ""} onChange={(ev) => setEditing({ ...e, text: ev.target.value })} data-testid="folders-text" />
              <span className="text-muted-foreground">As in the repository, from its top: no wildcards. At most 20.</span>
            </div>
            <div className="flex flex-wrap justify-end gap-2">
              {!e?.isNew && <Button type="button" variant="ghost" onClick={() => save.mutate({ coder_id: e.coder_id, folders: null })}
                disabled={save.isPending} data-testid="folders-lift">See everything</Button>}
              <Button type="submit" aria-disabled={!folders.length} disabled={save.isPending} data-testid="folders-save">Save</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProjectCard({ p, coders }) {
  const state = accessState(p);
  const [label, tone] = BADGE[state];
  return (
    <Card className="p-5 flex flex-col gap-4" data-testid={`project-card-${p.name}`}>
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div className="flex flex-col gap-1.5 min-w-0">
          <div className="flex items-center gap-2"><FolderGit2 className="w-4 h-4 shrink-0" />
            <span className="font-display text-[15px] leading-[1.35] tracking-[0.02em] font-semibold">{p.name}</span></div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
            {p.remote_kind === "github" ? (
              <span className="flex items-center gap-1.5"><Github className="w-3.5 h-3.5" />
                <a href={p.repo_url} target="_blank" rel="noreferrer" className="font-mono underline underline-offset-2 hover:opacity-50">{p.github_repo}</a></span>
            ) : (
              <span className="flex items-center gap-1.5 min-w-0"><FolderGit2 className="w-3.5 h-3.5 shrink-0" /><span className="font-mono truncate">{p.repo_url}</span></span>
            )}
            <span className="flex items-center gap-1.5"><GitBranch className="w-3.5 h-3.5" /><span>Base branch <span className="font-mono text-foreground">{p.base_branch}</span></span></span>
          </div>
        </div>
        <StatusBadge label={label} tone={tone} />
      </div>
      <Stats p={p} />
      {p.remote_kind === "github" ? <GitHubAccess p={p} state={state} /> : (
        <div className="pt-4 border-t text-sm text-muted-foreground [text-wrap:pretty]">
          A local demo repository on your workspace server. Sessions branch from it and push back to it; nothing goes to GitHub.
        </div>
      )}
      <FolderLimits p={p} coders={coders} />
    </Card>
  );
}

function NewProjectDialog({ open, onOpenChange, viaApp, github, manageUrl }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", base_branch: "main", github_repo: "" });
  const nameRef = useRef(null);
  useEffect(() => { if (open) setForm({ name: "", base_branch: "main", github_repo: "" }); }, [open]);
  const { data: repos } = useQuery({ queryKey: ["github-repos"], enabled: open && viaApp,
                                     queryFn: async () => (await api.get("/github/repos")).data.repositories });
  const create = useMutation({
    mutationFn: async () => (await api.post("/projects", { ...form, name: form.name.trim(), github_repo: form.github_repo.trim() })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      onOpenChange(false);
      toast.success(github && !viaApp ? "Project created. Set up its deploy key next." : "Project created.");
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const ready = form.name.trim() && (!github || form.github_repo.trim());
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-[18px]" onOpenAutoFocus={(e) => { e.preventDefault(); nameRef.current?.focus(); }}>
        <div className="flex flex-col gap-1">
          <DialogTitle>New project</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground [text-wrap:pretty]">
            A project is one repository, with its base branch and the memory its sessions share.
          </DialogDescription>
        </div>
        <form className="flex flex-col gap-[18px]" onSubmit={(e) => { e.preventDefault(); if (ready && !create.isPending) create.mutate(); }}>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="prj-name">Name</Label>
            <Input id="prj-name" ref={nameRef} data-testid="project-name" placeholder="e.g. api" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          {github && viaApp && (
            <div className="flex flex-col gap-1.5">
              <Label>GitHub repository</Label>
              <Select value={form.github_repo} onValueChange={(v) => {
                const r = repos?.find((x) => x.full_name === v);
                setForm((f) => ({ ...f, github_repo: v, base_branch: r?.default_branch || f.base_branch, name: f.name || v.split("/")[1] }));
              }}>
                <SelectTrigger data-testid="project-github-repo"><SelectValue placeholder={repos ? "Pick a repository" : "Loading…"} /></SelectTrigger>
                <SelectContent>{(repos || []).map((r) => <SelectItem key={r.full_name} value={r.full_name}>{r.full_name}</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">
                Not listed? Add it to the app on GitHub: <a className="underline underline-offset-2 hover:opacity-50" href={manageUrl} target="_blank" rel="noreferrer">choose repositories</a>.
              </span>
            </div>
          )}
          {github && !viaApp && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="prj-repo">GitHub repository</Label>
              <Input id="prj-repo" className="font-mono text-[12px]" data-testid="project-github-repo" placeholder="owner/name" value={form.github_repo}
                onChange={(e) => setForm({ ...form, github_repo: e.target.value })} />
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="prj-branch">Base branch</Label>
            <Input id="prj-branch" className="font-mono text-[12px]" data-testid="project-branch" value={form.base_branch}
              onChange={(e) => setForm({ ...form, base_branch: e.target.value })} />
            <span className="text-sm text-muted-foreground [text-wrap:pretty]">
              {github ? "Sessions branch from it and push to GitHub. Submitting a session opens a pull request with its change report."
                : "Sessions branch from it. Without GitHub, a local demo repository is made on your workspace server."}
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
            <span className="text-sm text-muted-foreground">{ready ? "" : github ? "Add a name and a repository to create it." : "Add a name to create it."}</span>
            <div className="flex gap-2 ml-auto">
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button type="submit" aria-disabled={!ready} disabled={create.isPending} data-testid="project-create-btn">Create project</Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Projects() {
  const flags = useFlags();
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const { data: team } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team")).data });
  const coders = (team?.members || []).filter((m) => m.role === "coder");
  const company = useCompanyName();
  const { connection } = useGitHub();
  const viaApp = flags.github_remotes && !!connection;
  const action = <Button onClick={() => setOpen(true)} data-testid="project-open-btn"><Plus />New project</Button>;
  const dialog = <NewProjectDialog open={open} onOpenChange={setOpen} viaApp={viaApp} github={!!flags.github_remotes} manageUrl={connection?.manage_url} />;
  const eyebrow = (n) => [company, n == null ? "Projects" : plural(n, "project")].filter(Boolean).join(" · ");
  if (isLoading || !data) return <Page><PageHeader eyebrow={eyebrow()} title="Projects" actions={action} /><Loading />{dialog}</Page>;

  const projects = data.projects;
  const allGitHub = projects.length > 0 && projects.every((p) => p.remote_kind === "github");
  const attention = projects.filter((p) => ["attention", "no-key"].includes(accessState(p))).length;
  return (
    <Page data-testid="owner-projects">
      <PageHeader eyebrow={eyebrow(projects.length)}
        title={projects.length ? `${plural(projects.length, "repository", "repositories")}${allGitHub ? " on GitHub" : ""}.` : "No repositories yet."}
        status={!projects.length ? "Add one to start sessions on it." : attention === 0 ? "Every repository is ready for sessions."
          : `${attention} ${attention === 1 ? "needs" : "need"} your attention.`}
        actions={action} />
      {projects.length === 0 && <Card className="px-5 py-6 text-muted-foreground">No projects yet. A project is one repository, with its base branch and the memory its sessions share.</Card>}
      {projects.map((p) => <ProjectCard key={p.id} p={p} coders={coders} />)}
      <PrivacyNote icon={GitBranch}>
        Each session works on its own branch from the base branch. {APP_NAME} makes its commits from metadata; the agent never commits or pushes.
      </PrivacyNote>
      {dialog}
    </Page>
  );
}
